/**
 * <ScreenErrorBoundary /> — React Error Boundary que captura erros de render
 * e oferece recovery + abertura direta de chamado pre-preenchido.
 *
 * Spec: CHAMADOS_VISIBILIDADE_SPEC §9.3.7 + §19.2 G-N4 (Zustand em vez de
 * CustomEvent) e §19.2 G-N12 (reset limpa window.__lastScreenError).
 *
 * Comportamento:
 *  - getDerivedStateFromError + componentDidCatch (Error Boundary classico)
 *  - Salva erro em window.__lastScreenError para FAB consumir
 *  - Fallback "full" (default): ícone, título, ID, botões "Tentar novamente" /
 *    "Abrir chamado deste erro"
 *  - Fallback "minimal": linha compacta + botão "Reportar"
 *  - Click "Abrir chamado deste erro" usa useReportModalStore.getState().open()
 *    com prefilledError populado (G-N4)
 *  - reset() limpa state E window.__lastScreenError (G-N12) — evita o FAB
 *    mostrar erro antigo após recovery
 */

import { Component, type ErrorInfo, type ReactNode } from 'react'
import { Stack, Text, Button, Code, Center, Box } from '@mantine/core'
import { IconAlertTriangle, IconBug, IconRefresh } from '@tabler/icons-react'
import { useReportModalStore } from '@/store/useReportModalStore'

interface ScreenErrorBoundaryProps {
  children: ReactNode
  fallback?: 'full' | 'minimal'
  screenName?: string
  onError?: (error: Error, errorInfo: ErrorInfo) => void
}

interface ScreenErrorBoundaryState {
  hasError: boolean
  error: Error | null
  errorId: string | null
}

function generateErrorId(): string {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID().slice(0, 8)
  }
  return Math.random().toString(36).slice(2, 10)
}

export class ScreenErrorBoundary extends Component<ScreenErrorBoundaryProps, ScreenErrorBoundaryState> {
  state: ScreenErrorBoundaryState = {
    hasError: false,
    error: null,
    errorId: null,
  }

  static getDerivedStateFromError(error: Error): Partial<ScreenErrorBoundaryState> {
    return {
      hasError: true,
      error,
      errorId: generateErrorId(),
    }
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    if (typeof window !== 'undefined') {
      window.__lastScreenError = {
        error,
        errorInfo: { componentStack: errorInfo.componentStack ?? '' },
        errorId: this.state.errorId ?? '',
      }
    }
    this.props.onError?.(error, errorInfo)
  }

  reset = () => {
    // G-N12: limpar window.__lastScreenError ao reset, evita FAB carregar
    // o erro antigo apos recovery bem-sucedido.
    if (typeof window !== 'undefined') {
      window.__lastScreenError = null
    }
    this.setState({ hasError: false, error: null, errorId: null })
  }

  openReportModal = () => {
    // G-N4: usar Zustand store (NAO CustomEvent) — evita problema de
    // multiplos listeners.
    useReportModalStore.getState().open({
      message: this.state.error?.message ?? 'Erro desconhecido',
      componentStack:
        typeof window !== 'undefined' ? window.__lastScreenError?.errorInfo.componentStack : undefined,
      errorId: this.state.errorId ?? undefined,
    })
  }

  render() {
    if (!this.state.hasError) return this.props.children

    const { fallback = 'full', screenName } = this.props
    const { error, errorId } = this.state

    if (fallback === 'minimal') {
      return (
        <Stack gap="xs" p="sm">
          <Text c="red.7" size="sm">
            Erro: {error?.message}
          </Text>
          <Stack gap={4}>
            <Button size="xs" leftSection={<IconBug size={14} />} onClick={this.openReportModal}>
              Reportar
            </Button>
            <Button
              size="xs"
              variant="default"
              leftSection={<IconRefresh size={14} />}
              onClick={this.reset}
            >
              Tentar novamente
            </Button>
          </Stack>
        </Stack>
      )
    }

    return (
      <Center mih="60vh">
        <Stack gap="md" align="center" maw={500}>
          <IconAlertTriangle size={64} color="var(--mantine-color-red-6)" />
          <Text size="xl" fw={600}>
            Algo deu errado
          </Text>
          {screenName && <Text c="dimmed">na tela &quot;{screenName}&quot;</Text>}
          <Box
            style={{
              background: 'var(--mantine-color-red-0)',
              padding: 12,
              borderRadius: 8,
              width: '100%',
            }}
          >
            <Text size="sm" c="red.7" fw={500}>
              {error?.message}
            </Text>
          </Box>
          {errorId && (
            <Text size="xs" c="dimmed">
              ID do erro: <Code>{errorId}</Code>
            </Text>
          )}
          <Stack gap="xs" w="100%">
            <Button leftSection={<IconBug size={16} />} onClick={this.openReportModal} fullWidth>
              Abrir chamado deste erro
            </Button>
            <Button
              variant="default"
              leftSection={<IconRefresh size={16} />}
              onClick={this.reset}
              fullWidth
            >
              Tentar novamente
            </Button>
          </Stack>
        </Stack>
      </Center>
    )
  }
}
